import { fetchMemberSubscription, isReadyPaidMembership } from 'actions/LuxPlusActions'
import { failPendingLuxPlusRenewalPoints, savePendingLuxPlusRenewalPoints } from 'checkout/lib/luxPlusRenewalPoints'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isActiveMemberWithSubscriptionTier } from 'luxPlus/lib/utils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { reportClientError } from 'services/errorReportingService'

export default function usePostPurchaseRenewalPoints(orderId: string | undefined, membershipPurchase: boolean) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const pending = useAppSelector(state => state.auth.account.luxPlus.member.pendingRenewalPoints)
  const subscription = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const pollStartedForOrder = useRef<string | undefined>(undefined)
  const hasPendingPreference = pending?.orderId === orderId
  const shouldPollMembership = membershipPurchase || (hasPendingPreference && !!pending?.membershipPurchase)

  const notifySaveFailure = useCallback(() => {
    showSnackbar(intl.formatMessage({
      id: 'iU3UUVigIOOPK6q3TyCg',
      defaultMessage: 'Your purchase is complete, but we couldn’t save your renewal points preference. You can update it in your LuxPlus+ account settings.',
      description: 'Error after a successful purchase when saving the renewal points preference fails, directing the member to account settings',
    }), 'critical')
  }, [intl])

  useEffect(() => {
    if (!orderId || !shouldPollMembership || subscription.fetching || pollStartedForOrder.current === orderId) return
    if (!hasPendingPreference && subscription.item && isReadyPaidMembership(subscription.item)) return

    const started = dispatch(fetchMemberSubscription({
      refetch: true,
      poll: true,
      validate: hasPendingPreference ? isReadyPaidMembership : isActiveMemberWithSubscriptionTier,
      onComplete: (result) => {
        if (result.status === 'failure' || (hasPendingPreference && (!result.data?.item || !isReadyPaidMembership(result.data.item)))) {
          const error = result.status === 'failure' ? result.error : new Error('LuxPlus membership is not ready')
          if (dispatch(failPendingLuxPlusRenewalPoints(orderId, error))) notifySaveFailure()
          else reportClientError(error)
          return
        }
        if (hasPendingPreference) {
          dispatch(savePendingLuxPlusRenewalPoints(orderId)).then((saved: boolean | undefined) => {
            if (saved === false) notifySaveFailure()
          })
        }
      },
    }))
    if (started) pollStartedForOrder.current = orderId
  }, [dispatch, hasPendingPreference, notifySaveFailure, orderId, shouldPollMembership, subscription.fetching, subscription.item])

  useEffect(() => {
    if (!orderId || shouldPollMembership || !hasPendingPreference) return
    dispatch(savePendingLuxPlusRenewalPoints(orderId)).then((saved: boolean | undefined) => {
      if (saved === false) notifySaveFailure()
    })
  }, [dispatch, hasPendingPreference, notifySaveFailure, orderId, shouldPollMembership])
}
